import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { VariantProps, cva } from "class-variance-authority";
import { Link, useLocation } from "react-router-dom";
import {
  LucideIcon, // Keep generic LucideIcon if used elsewhere
  PanelLeft,
  PanelLeftClose,
  PanelLeftOpen,
  MousePointer2,
  LineChart,
  Star,
  Tag,
  ClipboardList,
  Globe,
  Users,
  Award,
  Mail, // Import Mail icon
  Settings as SettingsIcon, // Renamed to avoid conflict
  Building, // Assuming this might be used for Business Management
  MessagesSquare, // Added for Reviews group
  Clock, // Added Clock icon
  List, // Added List icon
  DollarSign, // Added for Payroll
  Calendar, // Added for Google Calendar integration
  CalendarDays, // Added for My Events
  Briefcase, // Added for Job Postings
  BarChart3, // Added for Recruitment Analytics
  User, // Added for Account Settings
  Plus, // Added for Available Shifts
  Gift, // Added for Coupon Offers
  FileText, // Added for Contract Management
  ChevronRight, // Added for collapsible menu items
  Brain, // Added for AI Feedback Intelligence
  LayoutDashboard, // Added for Overview
  Bell, // Added for Alerts
  Share2, // Added for Social Media
  Send, // Added for Post Scheduling
  Inbox, // Added for Unified Inbox
  TrendingUp, // Added for Analytics
  MessageSquareReply, // Added for Auto-Reply
  Monitor, // Added for Digital Signage
  CalendarCheck, // Added for Meetings
  ArrowLeftRight, // Added for Shift Swaps
  CreditCard, // Added for Wallet Passes
  Upload, // Added for Import/Export
  Store, // Added for Commerce
  Package, // Added for Catalog
  Scissors, // Added for Services
  BookOpen, // Added for Menu
  CalendarRange, // Added for Bookings
  Warehouse, // Added for Inventory
} from "lucide-react";
import { CollapseChevron } from "@/components/ui/collapsible";
import { useIsMobile } from "@/hooks/ui/use-mobile";
import { cn } from '@/lib/core/utils';
import { useBusinessContext } from "@/context/BusinessContext";
import {
  type DashboardType,
  BusinessTabs, EmployeeTabs, PortalTabs, FranchiseTabs,
  isTabAllowedForPlan,
} from '@/lib/constants/dashboard';
import { useSubscription } from '@/context/SubscriptionContext';
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SimpleTooltip } from "@/components/overlays/simple-tooltip";
import SidebarBusinessManagement from "@/components/layout/sidebar-business-management"; // Keep if used elsewhere
import { supabase } from "@/integrations/supabase/client";
import { useAccountSettings } from "@/context/AccountSettingsContext";
import { useBusinessSettings } from "@/context/BusinessSettingsContext";

const SIDEBAR_COOKIE_NAME = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "12rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContext = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  displayMode: "always-expanded" | "always-collapsed" | "expand-on-hover";
  setDisplayMode: (mode: "always-expanded" | "always-collapsed" | "expand-on-hover") => void;
  isHovered: boolean;
  setIsHovered: (hovered: boolean) => void;
  pinnedOpen: boolean;
  setPinnedOpen: (pinned: boolean) => void;
};

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

const SIDEBAR_DISPLAY_MODE_COOKIE_NAME = "sidebar:display-mode";

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    forceExpanded?: boolean;
  }
>(({ defaultOpen = true, open: openProp, onOpenChange: setOpenProp, forceExpanded = false, className, style, children, ...props }, ref) => {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [pinnedOpen, setPinnedOpen] = React.useState(false);

  // Read displayMode from cookie on initial load
  const initialDisplayMode = React.useMemo(() => {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${SIDEBAR_DISPLAY_MODE_COOKIE_NAME}=`))
      ?.split('=')[1];

    if (cookieValue === "always-expanded" || cookieValue === "always-collapsed" || cookieValue === "expand-on-hover") {
      return cookieValue;
    }
    return "always-expanded"; // Default value
  }, []);

  const [displayMode, _setDisplayMode] = React.useState<"always-expanded" | "always-collapsed" | "expand-on-hover">(initialDisplayMode);

  const setDisplayMode = React.useCallback(
    (mode: "always-expanded" | "always-collapsed" | "expand-on-hover") => {
      _setDisplayMode(mode);
      document.cookie = `${SIDEBAR_DISPLAY_MODE_COOKIE_NAME}=${mode}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    []
  );

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open]
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);

  // Keyboard shortcut (Ctrl+B) is now handled by KeyboardShortcutsContext

  // Determine the effective state based on display mode and hover
  const getEffectiveState = () => {
    if (isMobile) return open ? "expanded" : "collapsed";

    switch (displayMode) {
      case "always-expanded":
        return "expanded";
      case "always-collapsed":
        return "collapsed";
      case "expand-on-hover":
        return (isHovered || forceExpanded || pinnedOpen) ? "expanded" : "collapsed";
      default:
        return open ? "expanded" : "collapsed";
    }
  };

  const state = getEffectiveState();

  const contextValue = React.useMemo<SidebarContext>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      displayMode,
      setDisplayMode,
      isHovered,
      setIsHovered,
      pinnedOpen,
      setPinnedOpen,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar, displayMode, setDisplayMode, isHovered, setIsHovered, pinnedOpen, setPinnedOpen]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          style={{
            "--sidebar-width": SIDEBAR_WIDTH,
            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties}
          className={cn(
            "group/sidebar-wrapper flex min-h-svh w-full has-[[data-variant=inset]]:bg-sidebar",
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
});
SidebarProvider.displayName = "SidebarProvider";

const Sidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
  }
>(({ side = "left", variant = "sidebar", collapsible = "offcanvas", className, children, ...props }, ref) => {
  const { isMobile, state, openMobile, setOpenMobile, setIsHovered, displayMode } = useSidebar();

  const handleMouseEnter = () => {
    if (!isMobile && displayMode === "expand-on-hover") {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && displayMode === "expand-on-hover") {
      setIsHovered(false);
    }
  };

  if (collapsible === "none") {
    return (
      <div
        className={cn(
          "flex h-full w-[--sidebar-width] flex-col bg-sidebar text-sidebar-foreground",
          className
        )}
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-mobile="true"
          className="w-[60vw] max-w-[280px] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
          side={side}
        >
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      ref={ref}
      className="group peer hidden md:block text-sidebar-foreground"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-display-mode={displayMode}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        className={cn(
          "duration-200 relative h-svh w-[--sidebar-width] bg-transparent transition-[width] ease-linear",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          // Keep collapsed width in expand-on-hover mode (overlay behavior)
          "group-data-[display-mode=expand-on-hover]:w-[--sidebar-width-icon]",
          // In expand-on-hover mode, put border on spacer (stays fixed) instead of expanding sidebar
          side === "left"
            ? "group-data-[display-mode=expand-on-hover]:border-r"
            : "group-data-[display-mode=expand-on-hover]:border-l",
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]"
            : "group-data-[collapsible=icon]:w-[--sidebar-width-icon]"
        )}
      />
      <div
        className={cn(
          "duration-200 fixed inset-y-0 z-30 hidden h-svh w-[--sidebar-width] transition-[left,right,width] ease-linear md:flex",
          side === "left"
            ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
            : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
          // Adjust the padding for floating and inset variants.
          variant === "floating" || variant === "inset"
            ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]"
            : "group-data-[collapsible=icon]:w-[--sidebar-width-icon] group-data-[side=left]:border-r group-data-[side=right]:border-l",
          // Remove border in expand-on-hover mode (border is on spacer instead)
          "group-data-[display-mode=expand-on-hover]:border-none",
          className
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          className="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow"
        >
          {children}
        </div>
      </div>
    </div>
  );
});
Sidebar.displayName = "Sidebar";

const SidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button>
>(({ className, onClick, variant = "outline", size = "icon", ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      ref={ref}
      data-sidebar="trigger"
      variant={variant}
      size={size}
      className={cn("h-10 md:h-8 w-10 md:w-8", className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeft />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
});
SidebarTrigger.displayName = "SidebarTrigger";

const SidebarRail = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button">
>(({ className, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <SimpleTooltip content="Toggle Sidebar">
    <Button
      ref={ref}
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      className={cn(
        "absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex",
        "[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-sidebar",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className
      )}
      {...props}
    />
    </SimpleTooltip>
  );
});
SidebarRail.displayName = "SidebarRail";

const SidebarInset = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"main">
>(({ className, ...props }, ref) => {
  const { state } = useSidebar();

  return (
    <main
      ref={ref}
      className={cn(
        "relative flex flex-1 flex-col bg-background w-full",
        "md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow",
        className
      )}
      {...props}
    />
  );
});
SidebarInset.displayName = "SidebarInset";

const SidebarInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  React.ComponentProps<typeof Input>
>(({ className, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        "h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
        className
      )}
      {...props}
    />
  );
});
SidebarInput.displayName = "SidebarInput";

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="header"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  );
});
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  );
});
SidebarFooter.displayName = "SidebarFooter";

const SidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn("mx-2 w-auto bg-sidebar-border", className)}
      {...props}
    />
  );
});
SidebarSeparator.displayName = "SidebarSeparator";

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      data-onboarding-id="sidebar-nav"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      {...props}
    />
  );
});
SidebarContent.displayName = "SidebarContent";

const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
      {...props}
    />
  );
});
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        "duration-200 flex h-10 md:h-8 shrink-0 items-center rounded-md px-2 md:px-2 text-sm md:text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opa] ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className
      )}
      {...props}
    />
  );
});
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        "absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  );
});
SidebarGroupAction.displayName = "SidebarGroupAction";

const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="group-content"
    className={cn("w-full text-sm", className)}
    {...props}
  />
));
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu"
    className={cn("flex w-full min-w-0 flex-col gap-1", className)}
    {...props}
  />
));
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    data-sidebar="menu-item"
    className={cn("group/menu-item relative", className)}
    {...props}
  />
));
SidebarMenuItem.displayName = "SidebarMenuItem";

const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2.5 md:p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding,background-color] hover:bg-primary/10 dark:hover:bg-primary/15 hover:text-sidebar-accent-foreground focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-primary/15 dark:data-[active=true]:bg-primary/25 data-[active=true]:font-semibold data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-primary/10 dark:data-[state=open]:hover:bg-primary/15 data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-foreground",
  {
    variants: {
      variant: {
        default: "hover:bg-primary/10 dark:hover:bg-primary/15 hover:text-sidebar-accent-foreground",
        outline:
          "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-primary/10 dark:hover:bg-primary/15 hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
      },
      size: {
        default: "h-10 md:h-8 text-sm",
        sm: "h-9 md:h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:!p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  } & VariantProps<typeof sidebarMenuButtonVariants>
>(
  ({
    asChild = false,
    isActive = false,
    variant = "default",
    size = "default",
    tooltip,
    className,
    ...props
  },
  ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const { isMobile, state, displayMode } = useSidebar();

    const button = (
      <Comp
        ref={ref}
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        className={cn(
          sidebarMenuButtonVariants({ variant, size }),
          className
        )}
        {...props}
      />
    );

    // Show tooltip when collapsed or when displayMode is always-collapsed
    const shouldShowTooltip = (state === "collapsed" && displayMode !== "always-expanded") || displayMode === "always-collapsed";

    if (!tooltip || !shouldShowTooltip) {
      return button;
    }

    if (typeof tooltip === "string") {
      tooltip = {
        children: tooltip,
      };
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={state === "expanded" && displayMode !== "always-collapsed"}
          {...tooltip}
        />
      </Tooltip>
    );
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";

const SidebarMenuAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    asChild?: boolean;
    showOnHover?: boolean;
  }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-action"
      className={cn(
        "absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
          "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0",
        className
      )}
      {...props}
    />
  );
});
SidebarMenuAction.displayName = "SidebarMenuAction";

const SidebarMenuBadge = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="menu-badge"
    className={cn(
      "absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none",
      "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
      "peer-data-[size=sm]/menu-button:top-1",
      "peer-data-[size=default]/menu-button:top-1.5",
      "peer-data-[size=lg]/menu-button:top-2.5",
      "group-data-[collapsible=icon]:hidden",
      className
    )}
    {...props}
  />
));
SidebarMenuBadge.displayName = "SidebarMenuBadge";

const SidebarMenuSkeleton = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    showIcon?: boolean;
  }
>(({ className, showIcon = false, ...props }, ref) => {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      ref={ref}
      data-sidebar="menu-skeleton"
      className={cn("rounded-md h-8 flex gap-2 px-2 items-center", className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 flex-1 max-w-[--skeleton-width]"
        data-sidebar="menu-skeleton-text"
        style={{
          "--skeleton-width": width,
        } as React.CSSProperties}
      />
    </div>
  );
});
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";

const SidebarMenuSub = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu-sub"
    className={cn(
      "mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5",
      "group-data-[collapsible=icon]:hidden",
      className
    )}
    {...props}
  />
));
SidebarMenuSub.displayName = "SidebarMenuSub";

const SidebarMenuSubItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ ...props }, ref) => <li ref={ref} {...props} />);
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

const SidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<"a"> & {
    asChild?: boolean;
    size?: "sm" | "md";
    isActive?: boolean;
  }
>(({ asChild = false, size = "md", isActive, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "flex h-10 md:h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 md:px-2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-primary/10 dark:hover:bg-primary/15 hover:text-sidebar-accent-foreground focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground",
        "data-[active=true]:bg-primary/15 dark:data-[active=true]:bg-primary/25 data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  );
});
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

// Define the new navigation structure for the dashboard
type NavItem = {
  id: string; // Unique ID for state management
  href?: string; // Optional href for direct links
  icon: React.ElementType;
  label: string;
  tooltip?: string; // Tooltip for collapsed state
  onClick?: () => void; // Handler for items that don't navigate directly
  view?: string; // Add view property for tab management
  subItems?: NavItem[]; // Optional sub-items for collapsible menus
  disabled?: boolean; // Optional disabled state
};

type NavGroup = {
  group: string;
  icon?: React.ElementType; // Optional icon for the group label
  items: NavItem[];
};

// Map tab values to IDs used in navigation state
export const businessDashboardNavigation: NavGroup[] = [
  {
    group: 'Overview',
    icon: LayoutDashboard,
    items: [
      { id: BusinessTabs.OVERVIEW, icon: LayoutDashboard, label: 'Overview', tooltip: 'Business Overview' },
    ]
  },
  {
    group: 'Reviews',
    icon: MessagesSquare,
    items: [
      {
        id: BusinessTabs.REVIEWS,
        icon: Star,
        label: 'Reviews',
        tooltip: 'Reviews',
        view: BusinessTabs.REVIEWS,
        subItems: [
          { id: BusinessTabs.REVIEWS, icon: Star, label: 'Reviews', tooltip: 'Reviews' },
          { id: BusinessTabs.INSIGHTS, icon: LineChart, label: 'Analytics', tooltip: 'Review Analytics & Trends' },
          { id: BusinessTabs.FORMS, icon: ClipboardList, label: 'Forms', tooltip: 'Review Forms' },
          { id: BusinessTabs.GOOGLE, icon: Globe, label: 'Google', tooltip: 'Google Reviews' },
          { id: BusinessTabs.FEEDBACK_INTELLIGENCE, icon: Brain, label: 'AI Intel', tooltip: 'AI-Powered Feedback Intelligence' },
        ]
      },
    ]
  },
      {
        group: 'Customer tools',
        icon: Users,
        items: [
          {
            id: BusinessTabs.CONTACTS,
            icon: Users,
            label: 'Contacts',
            tooltip: 'Contacts',
            view: BusinessTabs.CONTACTS,
            subItems: [
              { id: BusinessTabs.CONTACTS, icon: Users, label: 'Contacts', tooltip: 'Contact List' },
              { id: BusinessTabs.CONTACTS_TAGS, icon: Tag, label: 'Tags', tooltip: 'Contact Tags' },
              { id: BusinessTabs.CONTACTS_BUSYNESS, icon: LineChart, label: 'Busyness', tooltip: 'Busyness Analytics' },
              { id: BusinessTabs.CONTACTS_IMPORT_EXPORT, icon: Upload, label: 'Import/Export', tooltip: 'Import/Export (Coming Soon)', disabled: true },
            ]
          },
          {
            id: 'wallet-passes',
            icon: CreditCard,
            label: 'Wallet Passes',
            tooltip: 'Wallet Passes',
            view: BusinessTabs.LOYALTY,
            subItems: [
              { id: BusinessTabs.LOYALTY, icon: Award, label: 'Loyalty Cards', tooltip: 'Loyalty Cards' },
              { id: BusinessTabs.COUPONS, icon: Gift, label: 'Coupons', tooltip: 'Coupons' },
              { id: BusinessTabs.WALLET_STATISTICS, icon: BarChart3, label: 'Analytics', tooltip: 'Wallet Pass Analytics' },
            ]
          },
          {
            id: 'email',
            icon: Mail,
            label: 'Email',
            tooltip: 'Email Marketing',
            view: BusinessTabs.BULK_MAIL,
            subItems: [
              { id: BusinessTabs.BULK_MAIL, icon: Mail, label: 'Bulk Mail', tooltip: 'Bulk Mail' },
              { id: BusinessTabs.EMAIL_TEMPLATES, icon: Mail, label: 'Templates', tooltip: 'Email Templates' },
            ]
          },
          {
            id: 'social',
            icon: Share2,
            label: 'Social',
            tooltip: 'Social Media Management',
            view: BusinessTabs.SOCIAL_ACCOUNTS,
            subItems: [
              { id: BusinessTabs.SOCIAL_ACCOUNTS, icon: Share2, label: 'Accounts', tooltip: 'Connected Social Media Accounts' },
              { id: BusinessTabs.SOCIAL_POSTS, icon: Send, label: 'Posts', tooltip: 'Create & Schedule Posts' },
              { id: BusinessTabs.SOCIAL_INBOX, icon: Inbox, label: 'Inbox', tooltip: 'Unified Social Media Inbox' },
              { id: BusinessTabs.SOCIAL_ANALYTICS, icon: TrendingUp, label: 'Analytics', tooltip: 'Social Media Analytics' },
              { id: BusinessTabs.SOCIAL_AUTO_REPLY, icon: MessageSquareReply, label: 'Auto-Reply', tooltip: 'Auto-Reply Rules' },
            ]
          },
        ]
      },
      {
        group: 'Digital Signage',
        icon: Monitor,
        items: [
          { id: BusinessTabs.SIGNAGE, icon: Monitor, label: 'Displays', tooltip: 'Manage TV Displays & Content' },
        ]
      },
];

// Define navigation for Employee Dashboard
export const employeeDashboardNavigation: NavGroup[] = [
  {
    group: 'Employee Management',
    icon: Users,
    items: [
      { id: EmployeeTabs.INSIGHTS, icon: LayoutDashboard, label: 'Overview', tooltip: 'Operations Overview' },
      { id: EmployeeTabs.TIME_TRACKING, icon: LineChart, label: 'Time Tracking', tooltip: 'Time Tracking' },
      { id: EmployeeTabs.SHIFT_MANAGEMENT, icon: Clock, label: 'Shift Management', tooltip: 'Shift Management' },
      { id: EmployeeTabs.PAYROLL, icon: DollarSign, label: 'Payments', tooltip: 'Payroll Management' },
      { id: EmployeeTabs.FINANCIAL, icon: FileText, label: 'Financial', tooltip: 'Financial Management' },
      { id: EmployeeTabs.EMPLOYEE_LIST, icon: Users, label: 'Employees', tooltip: 'Employee List' },
      {
        id: EmployeeTabs.MEETINGS,
        icon: CalendarCheck,
        label: 'Meetings',
        tooltip: 'Personal calendar, meeting requests & scheduling',
        subItems: [
          { id: EmployeeTabs.MEETINGS_CALENDAR, icon: Calendar, label: 'Calendar', tooltip: 'Personal calendar & scheduling' },
          { id: EmployeeTabs.MEETINGS_PAGES, icon: FileText, label: 'Pages', tooltip: 'Personal notes & pages' },
        ]
      },
      {
        id: EmployeeTabs.COMMERCE,
        icon: Store,
        label: 'Commerce',
        tooltip: 'Products, services, bookings & inventory',
        subItems: [
          { id: EmployeeTabs.COMMERCE_CATALOG, icon: Package, label: 'Catalog', tooltip: 'Products & item catalog' },
          { id: EmployeeTabs.COMMERCE_SERVICES, icon: Scissors, label: 'Services', tooltip: 'Bookable services' },
          { id: EmployeeTabs.COMMERCE_MENU, icon: BookOpen, label: 'Menu', tooltip: 'Menu builder' },
          { id: EmployeeTabs.COMMERCE_BOOKINGS, icon: CalendarRange, label: 'Bookings', tooltip: 'Reservations & appointments' },
          { id: EmployeeTabs.COMMERCE_INVENTORY, icon: Warehouse, label: 'Inventory', tooltip: 'Stock management' },
        ]
      },
      // Hidden - not yet developed, low priority:
      // { id: EmployeeTabs.EVENTS, icon: Calendar, label: 'Events', tooltip: 'Event Management' },
      // { id: EmployeeTabs.JOB_POSTINGS, icon: Briefcase, label: 'Job Postings', tooltip: 'Job Postings & Recruitment' },
      { id: EmployeeTabs.COMMUNICATIONS, icon: MessagesSquare, label: 'WhatsApp', tooltip: 'WhatsApp Business Messaging' },
    ]
  },
];

// Define navigation for Employee Portal
// Note: Available Shifts and Shift Swaps are now integrated into the My Shifts calendar view
export const employeePortalNavigation: NavGroup[] = [
  {
    group: 'My Work',
    icon: User,
    items: [
      { id: PortalTabs.MY_DASHBOARD, icon: Clock, label: 'My Dashboard', tooltip: 'Current shift status, clock in/out and QR code' },
      { id: PortalTabs.MY_SHIFTS, icon: Calendar, label: 'My Shifts', tooltip: 'View my scheduled shifts, available shifts, and swap options' },
      { id: PortalTabs.MY_PAYROLL, icon: DollarSign, label: 'My Payroll', tooltip: 'View my earnings and payroll information' },
      { id: PortalTabs.MY_EVENTS, icon: CalendarDays, label: 'My Events', tooltip: 'View my events and schedule' },
      { id: PortalTabs.CONVERSATIONS, icon: MessagesSquare, label: 'WhatsApp', tooltip: 'WhatsApp Business Messaging' },
    ]
  },
];

// Define navigation for Franchise Command Center
export const franchiseDashboardNavigation: NavGroup[] = [
  {
    group: 'Portfolio',
    icon: Building,
    items: [
      { id: FranchiseTabs.OVERVIEW, icon: LayoutDashboard, label: 'Overview', tooltip: 'Portfolio Overview' },
      { id: FranchiseTabs.EMPLOYEES, icon: Users, label: 'Employees', tooltip: 'All Employees' },
      { id: FranchiseTabs.SCHEDULE, icon: Calendar, label: 'Schedule', tooltip: 'Cross-Venue Schedule' },
      { id: FranchiseTabs.ANALYTICS, icon: BarChart3, label: 'Analytics', tooltip: 'Portfolio Analytics' },
      { id: FranchiseTabs.PAYROLL, icon: DollarSign, label: 'Payroll', tooltip: 'Consolidated Payroll' },
      { id: FranchiseTabs.ALERTS, icon: Bell, label: 'Alerts', tooltip: 'Alert Center' },
    ]
  },
];

// Same hover menu as the navbar account and business settings buttons.
const CollapsedSubMenu: React.FC<{
  item: NavItem;
  hasActiveSubItem: (item: NavItem) => boolean;
  handleItemClick: (item: NavItem) => void;
}> = ({ item, hasActiveSubItem, handleItemClick }) => {
  const [open, setOpen] = React.useState(false);

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={0} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div>
          <SidebarMenuButton
            isActive={hasActiveSubItem(item)}
            tooltip={!open ? (item.tooltip || item.label) : undefined}
          >
            <item.icon />
            <span className="flex-1">{item.label}</span>
            <ChevronRight className="h-4 w-4" />
          </SidebarMenuButton>
        </div>
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-52 p-1 bg-background">
        <div className="mb-1 px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">{item.label}</div>
        <ButtonGroup orientation="vertical" className="w-full">
          {item.subItems!.map((subItem) => (
            <Button
              key={subItem.id}
              variant="outline"
              size="sm"
              className="w-full justify-start"
              disabled={subItem.disabled}
              onClick={() => { if (!subItem.disabled) { handleItemClick(subItem); setOpen(false); } }}
            >
              {subItem.icon && <subItem.icon className="h-4 w-4" />}
              {subItem.label}
            </Button>
          ))}
        </ButtonGroup>
      </HoverCardContent>
    </HoverCard>
  );
};

// New SidebarNavigation component tailored for the dashboard
interface DashboardSidebarNavigationProps extends React.ComponentProps<"div"> {
  activeView: string;
  setActiveView: (view: string) => void;
  dashboardType: DashboardType;
}

const DashboardSidebarNavigation = React.forwardRef<
  HTMLDivElement,
  DashboardSidebarNavigationProps
>(({ className, activeView, setActiveView, dashboardType, ...props }, ref) => {
  const location = useLocation(); // Use location if needed for highlighting based on path
  const { isMobile, setOpenMobile } = useSidebar(); // Get isMobile and setOpenMobile from context
  const [openMenus, setOpenMenus] = React.useState<Record<string, boolean>>({});
  // Use stable data reference instead of unstable function from useFeatureGating()
  // to prevent navigationData from recomputing on every render
  const { selectedBusiness } = useBusinessContext();
  const { currentPlan } = useSubscription();
  const enabledFeatures = selectedBusiness?.enabled_features;
  const planName = currentPlan?.name;

  // Track feature flags for employee portal
  const [employeePortalFeatures, setEmployeePortalFeatures] = React.useState<{
    paymentProcessingEnabled: boolean;
    allowShiftSelfAssignment: boolean;
    enableShiftSwapping: boolean;
  } | null>(null);

  // Fetch feature flags for employee-portal users via employee_assignments
  React.useEffect(() => {
    if (dashboardType !== "employee-portal") {
      return;
    }

    const fetchFeatureFlags = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) return;

        // Get employee's active business via assignments
        const { data: assignmentData } = await supabase
          .from('employee_assignments')
          .select('business_id, business:businesses!employee_assignments_business_id_fkey(payment_processing_enabled, allow_shift_self_assignment, enable_shift_swapping)')
          .eq('employee_id', (
            await supabase.from('employees').select('id').eq('user_id', user.id).single()
          ).data?.id ?? '')
          .limit(1)
          .single();

        const biz = (assignmentData as any)?.business;
        setEmployeePortalFeatures({
          paymentProcessingEnabled: biz?.payment_processing_enabled || false,
          allowShiftSelfAssignment: biz?.allow_shift_self_assignment || false,
          enableShiftSwapping: biz?.enable_shift_swapping || false,
        });
      } catch (error) {
        console.error('Error fetching feature flags:', error);
        setEmployeePortalFeatures({
          paymentProcessingEnabled: false,
          allowShiftSelfAssignment: false,
          enableShiftSwapping: false,
        });
      }
    };

    fetchFeatureFlags();
  }, [dashboardType]);

  const baseNavigationData =
    dashboardType === "employee" ? employeeDashboardNavigation :
    dashboardType === "employee-portal" ? employeePortalNavigation :
    dashboardType === "franchise" ? franchiseDashboardNavigation :
    businessDashboardNavigation; // Select navigation data based on type

  // Filter navigation items based on business-level feature visibility
  const navigationData = React.useMemo(() => {
    const filterByPlan = <T extends { items: { id: string; subItems?: { id: string }[] }[] }>(groups: T[]) => {
      if (planName !== 'free') return groups;
      return groups
        .map(group => ({
          ...group,
          items: group.items
            .map(item => ({
              ...item,
              subItems: item.subItems?.filter(subItem => isTabAllowedForPlan(planName, dashboardType, subItem.id)),
            }))
            .filter(item => item.subItems
              ? item.subItems.length > 0
              : isTabAllowedForPlan(planName, dashboardType, item.id)),
        }))
        .filter(group => group.items.length > 0);
    };

    // For employee-portal, filter tabs based on business feature flags
    if (dashboardType === "employee-portal") {
      return baseNavigationData.map(group => ({
        ...group,
        items: group.items.filter(item => {
          // Hide my-payroll if payment processing is not enabled
          if (item.id === 'my-payroll' && !employeePortalFeatures?.paymentProcessingEnabled) {
            return false;
          }
          // Note: available-shifts and shift-swaps are now integrated into my-shifts calendar view
          // and are conditionally shown there based on business settings
          return true;
        }),
      }));
    }

    if (dashboardType !== "business") {
      return filterByPlan(baseNavigationData);
    }

    // Inline the feature check using stable enabledFeatures reference
    const isEnabled = (featureName: string): boolean => {
      if (!enabledFeatures) return true;
      return enabledFeatures[featureName] !== false;
    };

    return filterByPlan(baseNavigationData.map(group => ({
      ...group,
      items: group.items
        .filter(item => isEnabled(item.id))
        .map(item => ({
          ...item,
          // Also filter sub-items if present
          subItems: item.subItems?.filter(subItem =>
            isEnabled(subItem.id)
          ),
        }))
        // Remove parent items that have no visible sub-items left
        .filter(item => !item.subItems || item.subItems.length > 0),
    }))
    // Remove empty groups
    .filter(group => group.items.length > 0));
  }, [baseNavigationData, dashboardType, enabledFeatures, employeePortalFeatures, planName]);

  // Helper to determine if an item should be active
  const isNavItemActive = (item: NavItem) => {
    // Prioritize activeView state for highlighting
    if (activeView) {
      return item.id === activeView;
    }
    // Fallback to href matching if needed (though onClick is preferred here)
    return item.href && location.pathname.startsWith(item.href);
  };

  // Helper to check if any sub-item is active
  const hasActiveSubItem = (item: NavItem) => {
    if (!item.subItems) return false;
    return item.subItems.some(subItem => isNavItemActive(subItem));
  };

  // Auto-open menus for items with active sub-items
  React.useEffect(() => {
    setOpenMenus(prev => {
      const toOpen: string[] = [];
      navigationData.forEach(group => {
        group.items.forEach(item => {
          if (item.subItems && hasActiveSubItem(item) && !prev[item.id]) {
            toOpen.push(item.id);
          }
        });
      });
      // Return same reference if nothing changed to avoid unnecessary re-renders
      if (toOpen.length === 0) return prev;
      const next = { ...prev };
      toOpen.forEach(id => { next[id] = true; });
      return next;
    });
  }, [activeView, navigationData]);

  const handleItemClick = (item: NavItem) => {
    if (item.onClick) {
      item.onClick(); // Execute custom onClick if provided
    } else if (item.view) {
      // If item has a view property (for parent items with subItems), use that
      setActiveView(item.view);
    } else if (item.subItems && item.subItems.length > 0) {
      // If item has sub-items, navigate to the first sub-item by default
      setActiveView(item.subItems[0].id);
    } else if (item.id) {
      setActiveView(item.id); // Set the active view based on item ID
    }
    // Close sidebar on mobile after clicking an item
    if (isMobile) {
      setOpenMobile(false);
    }
    // If item.href is present, navigation will be handled by the Link component
  };

  const toggleMenu = (itemId: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const { state, displayMode } = useSidebar();
  const isCollapsed = state === "collapsed" && displayMode !== "always-expanded";

  return (
    <div
      ref={ref}
      data-sidebar="navigation"
      className={cn("flex flex-col", className)}
      {...props}
    >
      {navigationData.map((group) => ( // Use selected navigationData
        <SidebarGroup key={group.group}>
          <SidebarMenu>
            {group.items.map((item) => (
              <SidebarMenuItem key={item.id}>
                {item.subItems ? (
                  isCollapsed ? (
                    /* Collapsed: show sub-items in a dropdown menu on hover */
                    <CollapsedSubMenu
                      item={item}
                      hasActiveSubItem={hasActiveSubItem}
                      handleItemClick={handleItemClick}
                    />
                  ) : (
                  /* Expanded: show inline collapsible submenu */
                  <>
                    <SidebarMenuButton
                      isActive={false}
                      tooltip={item.tooltip || item.label}
                      onClick={(e) => {
                        // Navigate to default view AND toggle dropdown open
                        handleItemClick(item);
                        if (!openMenus[item.id]) {
                          toggleMenu(item.id);
                        }
                      }}
                      className="pr-0"
                    >
                      <item.icon />
                      <span className="flex-1">{item.label}</span>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMenu(item.id);
                        }}
                        className="p-1 hover:bg-sidebar-accent rounded-sm transition-colors cursor-pointer"
                        role="button"
                        aria-label={`Toggle ${item.label} submenu`}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleMenu(item.id);
                          }
                        }}
                      >
                        <CollapseChevron open={!!openMenus[item.id]} />
                      </span>
                    </SidebarMenuButton>
                    {openMenus[item.id] && (
                      <SidebarMenuSub>
                        {item.subItems.map((subItem) => (
                          <SidebarMenuSubItem key={subItem.id}>
                            <SidebarMenuSubButton
                              asChild={!!subItem.href && !subItem.disabled}
                              isActive={isNavItemActive(subItem)}
                              onClick={!subItem.href && !subItem.disabled ? () => handleItemClick(subItem) : undefined}
                              className={cn(
                                !subItem.disabled && "cursor-pointer",
                                subItem.disabled && "cursor-not-allowed opacity-50 pointer-events-none"
                              )}
                            >
                              {subItem.href && !subItem.disabled ? (
                                <Link to={subItem.href} onClick={() => handleItemClick(subItem)}>
                                  <span>{subItem.label}</span>
                                </Link>
                              ) : (
                                <span>{subItem.label}</span>
                              )}
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    )}
                  </>
                  )
                ) : (
                  <SidebarMenuButton
                    asChild={!!item.href} // Use asChild only if href exists
                    isActive={isNavItemActive(item)}
                    tooltip={item.tooltip || item.label}
                    onClick={!item.href ? () => handleItemClick(item) : undefined} // Set onClick only if no href
                  >
                    {item.href ? (
                      <Link to={item.href} onClick={() => handleItemClick(item)}>
                        <item.icon />
                        <span>{item.label}</span>
                      </Link>
                    ) : (
                      <>
                        <item.icon />
                        <span>{item.label}</span>
                      </>
                    )}
                  </SidebarMenuButton>
                )}
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </div>
  );
});
DashboardSidebarNavigation.displayName = "DashboardSidebarNavigation";

// Remove the old SidebarNavigationWrapper if it's no longer needed
// const SidebarNavigationWrapper = ({ children }: { children: React.ReactNode }) => {
//   return (
//     <>
//       <SidebarNavigation /> // Assuming old one is removed or renamed
//       {children}
//     </>
//   );
// };

const SidebarSettingsButtons = () => {
  const { isMobile } = useSidebar();
  const location = useLocation();
  const { isAccountSettingsOpen, openAccountSettings, closeAccountSettings } = useAccountSettings();
  const { isBusinessSettingsOpen, openBusinessSettings, closeBusinessSettings } = useBusinessSettings();
  const { selectedBusinessId } = useBusinessContext();

  if (isMobile) return null;

  const isDashboardPage =
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/employee-dashboard') ||
    location.pathname.startsWith('/portfolio') ||
    location.pathname.startsWith('/settings/');

  const showBusinessSettings = !!selectedBusinessId && isDashboardPage;

  const handleAccountClick = () => {
    if (isAccountSettingsOpen) closeAccountSettings();
    else openAccountSettings();
  };

  const handleBusinessClick = () => {
    if (isBusinessSettingsOpen) closeBusinessSettings();
    else openBusinessSettings();
  };

  return (
    <>
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip="Account Settings"
          isActive={isAccountSettingsOpen}
          onClick={handleAccountClick}
        >
          <User />
          <span>Account Settings</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
      {showBusinessSettings && (
        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip="Business Settings"
            isActive={isBusinessSettingsOpen}
            onClick={handleBusinessClick}
          >
            <SettingsIcon />
            <span>Business Settings</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )}
    </>
  );
};
SidebarSettingsButtons.displayName = "SidebarSettingsButtons";

const SidebarModeToggle = () => {
  const { displayMode, setDisplayMode, isMobile, setPinnedOpen } = useSidebar();
  const [open, setOpen] = React.useState(false);

  if (isMobile) return null;

  const modes = [
    { value: "always-expanded", label: "Expanded", icon: PanelLeftOpen },
    { value: "always-collapsed", label: "Collapsed", icon: PanelLeftClose },
    { value: "expand-on-hover", label: "Expand on hover", icon: MousePointer2 },
  ] as const;

  return (
    <SidebarMenuItem>
      <HoverCard
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          setPinnedOpen(next);
        }}
        openDelay={0}
        closeDelay={100}
      >
        <HoverCardTrigger asChild>
          <div>
            <SidebarMenuButton tooltip={!open ? "Sidebar Settings" : undefined}>
              <PanelLeft />
              <span>Sidebar Settings</span>
            </SidebarMenuButton>
          </div>
        </HoverCardTrigger>
        <HoverCardContent side="right" align="end" className="w-48 p-1 bg-background">
          <div className="mb-1 px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">Sidebar Settings</div>
          <ButtonGroup orientation="vertical" className="w-full">
            {modes.map((mode) => (
              <Button
                key={mode.value}
                variant={displayMode === mode.value ? "default" : "outline"}
                size="sm"
                className="w-full justify-start"
                onClick={() => setDisplayMode(mode.value)}
              >
                <mode.icon className="h-4 w-4" />
                {mode.label}
              </Button>
            ))}
          </ButtonGroup>
        </HoverCardContent>
      </HoverCard>
    </SidebarMenuItem>
  );
};
SidebarModeToggle.displayName = "SidebarModeToggle";

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  SidebarModeToggle,
  SidebarSettingsButtons,
  useSidebar,
  DashboardSidebarNavigation,
};
